import { LoadingBox } from '@revelio/core';
import { Button, DetailsWithDescription, Option, Text } from '@revelio/ui';

import { useDataBuilderQuery } from '../api/useDataBuilderQuery';
import { DataBuilderFooter } from '../components/DataBuilderFooter';
import { DataBuilderGrid } from '../components/ui/DataBuilderGrid';
import { DataBuilderOption } from '../components/ui/DataBuilderOption';
import { DataBuilderStepLayout } from '../components/ui/DataBuilderStepLayout';
import {
  selectDatasetInstances,
  selectSelectedDatasetKeys,
  useDataBuilderActions,
  useDataBuilderSelector,
} from '../data-builder.store';
import {
  type DataBuilderDataset,
  groupDatasetsByGroupId,
} from '../datasets/api-transform';
import { seedInstanceDefaults } from '../datasets/initial-instance-config';
import { useDataBuilderNavigation } from '../useDataBuilderNavigation';

type DatasetCardProps = {
  dataset: DataBuilderDataset;
  isSelected: boolean;
  onClick: () => void;
};

const DatasetCard = ({ dataset, isSelected, onClick }: DatasetCardProps) => {
  return (
    <Option
      data-testid={`data-builder-dataset-option-${dataset.id}`}
      style={{ flex: 1, minHeight: 0, alignItems: 'flex-start' }}
      disabled={dataset.isDisabled}
      isSelected={isSelected}
      onClick={onClick}
      startSlot={
        <div style={{ marginTop: '0.5rem' }}>
          <DataBuilderOption.CheckBox isSelected={isSelected} />
        </div>
      }
      contentAlignment="start"
      startSlotAlignment="start"
    >
      <DetailsWithDescription
        title={dataset.label}
        detail={dataset.detail ?? ''}
        disabled={dataset.isDisabled}
      />
    </Option>
  );
};

export const DataBuilderDatasetStep = () => {
  const { data, isLoading } = useDataBuilderQuery();
  const selectedDatasetKeys = useDataBuilderSelector(selectSelectedDatasetKeys);
  const instances = useDataBuilderSelector(selectDatasetInstances);
  const { toggleDatasetType } = useDataBuilderActions();
  const { goToNextStep } = useDataBuilderNavigation();

  const datasetsByGroup =
    data !== undefined ? groupDatasetsByGroupId(data.datasets) : new Map();

  // Seed file/column defaults at toggle time so every instance enters the
  // store fully populated — Step 2 no longer needs to materialize defaults
  // in an effect, and the submit path can read state as-is.
  const handleToggle = (datasetKey: string) => {
    if (selectedDatasetKeys.includes(datasetKey)) {
      toggleDatasetType(datasetKey);
      return;
    }
    const deliverableConfig = data?.deliverablesConfig[datasetKey];
    const initialConfig =
      deliverableConfig !== undefined
        ? seedInstanceDefaults(deliverableConfig)
        : undefined;
    toggleDatasetType(datasetKey, initialConfig);
  };

  return (
    <DataBuilderStepLayout
      title="Select Datasets"
      footer={
        <DataBuilderFooter
          actions={
            <Button
              onClick={goToNextStep}
              disabled={instances.length === 0}
              color="primary"
              size="sm"
            >
              Continue
            </Button>
          }
        />
      }
    >
      <LoadingBox loading={isLoading}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {data?.groups.map((group) => {
            const groupDatasets = datasetsByGroup.get(group.id);
            if (!groupDatasets?.length) return null;
            return (
              <section
                key={group.id}
                style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                data-testid={`data-builder-dataset-group-${group.id}`}
              >
                <Text as="h3" fontSize="sm" weight="semibold">
                  {group.name}
                </Text>
                <DataBuilderGrid columns="2">
                  {groupDatasets.map((dataset: DataBuilderDataset) => (
                    <DatasetCard
                      key={dataset.id}
                      dataset={dataset}
                      isSelected={selectedDatasetKeys.includes(dataset.id)}
                      onClick={() => handleToggle(dataset.id)}
                    />
                  ))}
                </DataBuilderGrid>
              </section>
            );
          })}
        </div>
      </LoadingBox>
    </DataBuilderStepLayout>
  );
};
